/* eslint-disable max-lines */
import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import Brightness1Icon from '@material-ui/icons/Brightness1';
import FavoriteIcon from '@material-ui/icons/Favorite';
import SubdirectoryArrowLeftIcon from '@material-ui/icons/SubdirectoryArrowLeft';

import { translate } from 'services/i18nService';
import UserStore from 'stores/UserStore';
import { EPAY_PRODUCT_TYPES } from 'enum/payments';
import allUserGroups from 'enum/userGroups';
import {
  formatSEKValue,
  generateID,
  getFormattedAmount,
} from 'utils/helpers';

import styles from './PaymentRow.css';


export default class PaymentRow extends PureComponent {
  static propTypes = {
    isActive: PropTypes.bool.isRequired,
    isGoldUser: PropTypes.bool.isRequired,
    pack: PropTypes.object.isRequired,
    selectedMethod: PropTypes.object.isRequired,
    setActive: PropTypes.func.isRequired,
    user: PropTypes.object.isRequired,
    allActiveCampaigns: PropTypes.object,
    selectedCryptoCurrency: PropTypes.object,
  };

  static defaultProps = {
    allActiveCampaigns: undefined,
    selectedCryptoCurrency: undefined,
  };

  constructor(...args) {
    super(...args);

    this.IDs = {
      prefix: '2hdd7BmQ',
      element: 'paymentRow',
      paymentRow: 'paymentRow',
      strike: 'strike',
      amount: 'amount',
      price: 'price',
      bonus: 'bonus',
      save: 'save',
      bonusText: 'bonusText',
    };

    this.makeId = generateID(this.IDs.prefix, this.IDs.element);
  }

  checkGiftCard = () => {
    const { pack, selectedMethod } = this.props;

    const result = (
      !pack.amount &&
      !pack.quantity &&
      selectedMethod.value === 'GC'
    );

    return result;
  };

  isMembership = () => {
    const { pack } = this.props;

    return pack.type === EPAY_PRODUCT_TYPES.MEMBERSHIP;
  };

  isCountryGermany = () => {
    const { user: { realCountry } } = this.props;

    const result = (
      realCountry === 'de' ||
      UserStore.hasCF(allUserGroups.USER_COUNTRY_DE)
    );

    return result;
  };

  getAmount = (pack) => {
    const { selectedMethod } = this.props;

    let amount = parseFloat(pack.amount);
    if (selectedMethod.value !== 'CR' && pack.details && pack.details.tf) {
      amount = pack.amount - (pack.details.tx || 1);
    }

    return amount;
  };

  getPrimaryCampaign = () => {
    const { allActiveCampaigns } = this.props;

    return allActiveCampaigns?.size ? allActiveCampaigns.get(0) : null;
  };

  getBonusTokensCampaign = () => {
    if (window.hasPurchases === true || window.hasPurchases === undefined) {
      return null;
    }
    const { allActiveCampaigns } = this.props;

    let bonusTokensCampaign = null;
    if (allActiveCampaigns?.size) {
      // assuming there is just one campaign with bonus amount active for user (e.g. 50 tokens)
      const filteredCampaigns = allActiveCampaigns.filter(
        (campaign) => Boolean(campaign.get('campaignDetails').get('bonusTokenAmount'))
      );
      bonusTokensCampaign = filteredCampaigns.size ? filteredCampaigns.get(0) : null;
    }

    return bonusTokensCampaign;
  };

  getCampaignsWithPercentageBonus = () => {
    const { allActiveCampaigns } = this.props;

    let allCampaignsWithPercentageBonus = null;
    if (allActiveCampaigns?.size) {
      allCampaignsWithPercentageBonus = allActiveCampaigns.filter(
        (campaign) => Boolean(campaign.get('campaignDetails')?.get('bonusTokenPercent'))
      );
    }

    return allCampaignsWithPercentageBonus;
  };

  getMinumunPackValueForBonusTokensAmount = (bonusTokensCampaign, activeCampaign) => {
    const campaign = bonusTokensCampaign || activeCampaign;

    const details = campaign?.get?.('campaignDetails');
    if (details) {
      return details.get('minTokensValue') || 0;
    }

    return 0;
  };

  getMinumunPackValueForGoldBonus = (activeCampaign) => {
    const details = activeCampaign?.get?.('campaignDetails');
    if (details) {
      return details.get('goldMinTokensValue') || 0;
    }

    return 0;
  };

  getBonusTokensAmount = (bonusTokensCampaign, activeCampaign) => {
    if (window.hasPurchases === true || window.hasPurchases === undefined) {
      return null;
    }

    const { pack } = this.props;

    const minPackValue = this.getMinumunPackValueForBonusTokensAmount(bonusTokensCampaign, activeCampaign);
    const campaign = bonusTokensCampaign || activeCampaign;
    let amount = 0;

    if (minPackValue <= pack.quantity) {
      const details = campaign?.get?.('campaignDetails');
      if (details) {
        amount = details.get('bonusTokenAmount') || 0;
      }
    }

    return amount;
  };

  calculateTotalPercentageBonus = (allCampaignsWithPercentageBonus) => {
    const { pack } = this.props;

    let percentageBonuses = 0;
    if (allCampaignsWithPercentageBonus?.size) {
      percentageBonuses = allCampaignsWithPercentageBonus.reduce((sum, campaign) => {
        const bonus = +campaign.get('campaignDetails').get('bonusTokenPercent');
        const minTokensValue = campaign.get('campaignDetails').get('minTokensValue');
        const minimumPackageValue = minTokensValue ? parseInt(minTokensValue, 10) : 0;

        if (pack.quantity >= minimumPackageValue) {
          return sum + Math.ceil((pack.quantity / 100) * bonus);
        }

        return sum;
      }, 0);
    }

    return percentageBonuses;
  };

  calculateTotalCampaignAmount = (bonusTokenAmount, addedPercentageBonuses) => {
    const { pack } = this.props;

    let campaignAmount = 0;
    if (!this.isMembership()) {
      if (bonusTokenAmount) {
        campaignAmount = parseInt(pack.quantity + addedPercentageBonuses + bonusTokenAmount, 10);
      } else {
        campaignAmount = parseInt(pack.quantity + addedPercentageBonuses, 10);
      }
    }

    return campaignAmount;
  };

  getProcessorIdCC = () => {
    const { pack, selectedMethod } = this.props;

    let processorIdCC = '';
    pack.categories.forEach((category) => {
      category.methods.forEach((method) => {
        if (method.type === selectedMethod.value) {
          processorIdCC = method.processorId;
        }
      });
    });

    return processorIdCC;
  };

  formatAmount = (symbol, amount, country) => {
    // this currently covers only kr vs all other currencies
    if (symbol === 'kr') {
      return `${formatSEKValue(getFormattedAmount(amount, 1, symbol, true, symbol === 'kr'))} kr`;
    }

    return getFormattedAmount(amount, 1, symbol, true, symbol === 'kr', country);
  };

  amountRow = (campaignAmount) => {
    const { pack, selectedMethod } = this.props;

    const isGermany = this.isCountryGermany();
    const amount = this.getAmount(pack);
    const newTokensAmount = campaignAmount - parseInt(pack.quantity, 10) > 0;
    const showCrossedOutAmount = !this.isMembership() && newTokensAmount;

    let originalPackageContent = undefined;
    let formattedAmount = undefined;

    const isGiftCard = this.checkGiftCard();
    if (isGiftCard) {
      originalPackageContent = translate(`payment.processor.${selectedMethod.processorId}.tokens`);
    } else if (this.isMembership()) {
      originalPackageContent = pack.details && pack.details.bonus;
      formattedAmount = translate('epay.epay.free');
    } else {
      originalPackageContent = pack.quantity;
      formattedAmount = this.formatAmount(pack.currency.symbol, amount, isGermany);
    }

    return (
      <div className={styles.amount}>
        <div className={styles.tokens}>
          <span
            className={showCrossedOutAmount ? `${styles.campaignAmount} ${styles.strike}` : undefined}
            id={this.makeId(`${this.IDs.strike}_${pack.quantity}`)}
          >
            {originalPackageContent}
          </span>
          <span
            className={styles.campaignAmount}
            id={this.makeId(`${this.IDs.amount}_${pack.quantity}`)}
          >
            &nbsp;
            {!isGiftCard && showCrossedOutAmount && campaignAmount}
            {!isGiftCard && ` ${translate('epay.epay.tokens')}`}
          </span>
        </div>
        {formattedAmount && (
          <div
            className={this.isMembership() ? `${styles.price} ${styles.free}` : styles.price}
            id={this.makeId(`${this.IDs.price}_${pack.quantity}`)}
          >
            {formattedAmount}
          </div>
        )}
      </div>
    );
  };

  // eslint-disable-next-line complexity
  bonusesRow = () => {
    const { allActiveCampaigns, isGoldUser, pack } = this.props;

    const activeCampaign = this.getPrimaryCampaign();
    const bonusTokensCampaign = this.getBonusTokensCampaign();
    const allCampaignsWithPercentageBonus = this.getCampaignsWithPercentageBonus();
    const campaignDetails = activeCampaign?.get('campaignDetails');

    const NO_ACTIVE_CAMPAIGNS = !allActiveCampaigns || allActiveCampaigns.size === 0;

    const ONLY_BONUS_TOKENS_ACTIVE = (
      allActiveCampaigns?.size === 1 &&
      activeCampaign.get('activeCampaignName') === 'BONUS_TOKENS'
    );
    const ONLY_PERCENTAGE_BONUS_ACTIVE = (
      Boolean(allActiveCampaigns) &&
      Boolean(allCampaignsWithPercentageBonus) &&
      !bonusTokensCampaign
    );
    const COMBINED_CAMPAIGNS_ACTIVE = (
      allActiveCampaigns?.size > 1 &&
      Boolean(bonusTokensCampaign) &&
      Boolean(allCampaignsWithPercentageBonus)
    );

    // gold user bonus % from primary campaign
    const goldBonusPercent = campaignDetails?.get('goldBonusTokenPercent');

    // minimum package to apply bonus
    const minTokensValueForBonusAmount = this.getMinumunPackValueForBonusTokensAmount(
      bonusTokensCampaign,
      activeCampaign
    );

    const minTokensValueForGoldBonus = this.getMinumunPackValueForGoldBonus(activeCampaign);

    let goldMemberBonus = 0;
    if (activeCampaign && goldBonusPercent && pack.quantity >= minTokensValueForGoldBonus) {
      goldMemberBonus = Math.ceil(goldBonusPercent * (pack.quantity / 100));
    }

    // extra bonus e.g. for first purchase
    const bonusTokenAmount = this.getBonusTokensAmount(bonusTokensCampaign, activeCampaign);

    const addedPercentageBonuses = this.calculateTotalPercentageBonus(allCampaignsWithPercentageBonus);

    const COMBINED_CAMPAIGNS_BASIC_PARTIAL_BONUS =
      !isGoldUser && COMBINED_CAMPAIGNS_ACTIVE && addedPercentageBonuses === 0;

    const bonusTextForCombinedCampaigns = !this.isMembership() && (addedPercentageBonuses || bonusTokenAmount);

    // total tokens amount user gets (package baseline + bonuses)
    const campaignAmount = this.calculateTotalCampaignAmount(bonusTokenAmount, addedPercentageBonuses);

    return (
      <div className={styles.bonus} id={this.makeId(`${this.IDs.bonusText}_${pack.quantity}`)}>
        {this.isMembership() && translate('epay.epay.with.gold.membership')}
        {NO_ACTIVE_CAMPAIGNS && this.noCampaignContent()}
        {ONLY_BONUS_TOKENS_ACTIVE && this.bonusAmountContent(campaignAmount, minTokensValueForBonusAmount)}
        {ONLY_PERCENTAGE_BONUS_ACTIVE &&
          !this.isMembership() &&
          this.percetageBonusesContent(goldMemberBonus, campaignAmount)}
        {COMBINED_CAMPAIGNS_ACTIVE &&
          (addedPercentageBonuses > 0 || isGoldUser) &&
          Boolean(bonusTextForCombinedCampaigns) &&
          this.combinedBonusesContent(addedPercentageBonuses, bonusTokenAmount)}
        {COMBINED_CAMPAIGNS_BASIC_PARTIAL_BONUS && this.combinedBonusesForGold(bonusTokenAmount, goldMemberBonus)}
      </div>
    );
  };

  popularTag = (className) => {
    const { pack: { details } } = this.props;

    if (!details || !details.default) {
      return null;
    }

    return (
      <span className={styles[className]}>
        <FavoriteIcon className={styles.favoriteIcon} />
        <span className={styles.popularTextSpan}>
          {translate('epay.popular.tag')}
        </span>
      </span>
    );
  };

  noCampaignContent = () => {
    const { pack } = this.props;
    const { symbol } = pack?.currency || {};
    const isGermany = this.isCountryGermany();
    const savings = pack.details ? (pack.details.referencePrice * pack.quantity) - Math.ceil(this.getAmount(pack)) : 0;

    return (
      <Fragment>
        {savings > 0 && (
          <span className={styles.savings} id={this.makeId(`${this.IDs.save}_${pack.quantity}`)}>
            {translate('epay.epay.save')}{' '}
            {pack.currency.symbol === 'kr' ? `${formatSEKValue(
              getFormattedAmount(savings, 1, pack.currency.symbol, true, pack.currency.symbol === 'kr')
            )} kr` : getFormattedAmount(savings, 1, symbol, true, symbol === 'kr', isGermany)}
          </span>
        )}
        {this.popularTag('popularTagSaving')}
      </Fragment>
    );
  };

  bonusAmountContent = (campaignAmount, minTokensValueForBonusAmount) => {
    const { pack } = this.props;

    const bonus = campaignAmount ? parseInt(campaignAmount - pack.quantity, 10) : 0;
    const hideBonusTokensText = pack.quantity < minTokensValueForBonusAmount;

    return (
      <Fragment>
        <span
          className={hideBonusTokensText ? styles.hidden : styles.bonusTokensGold}
          id={this.makeId(`${pack.quantity}`)}
        >
          {translate(
            'payment.payment.amount.bonus.tokens',
            { 0: bonus }
          )}
        </span>
        {this.popularTag('popularTag')}
      </Fragment>
    );
  };

  percetageBonusesContent = (goldMemberBonus, campaignAmount) => {
    const { isGoldUser, pack } = this.props;

    const bonus = campaignAmount ? parseInt(campaignAmount - pack.quantity, 10) : 0;
    // all users get the same bonus
    const showTotalBonus = isGoldUser || !goldMemberBonus;
    if (showTotalBonus && !bonus) {
      return undefined;
    }

    return (
      <Fragment>
        {showTotalBonus ? (
          <div>
            <span
              className={styles.bonusTokensGold}
              id={this.makeId(`${pack.quantity}`)}
            >
              {translate(
                'payment.payment.amount.bonus.tokens',
                { 0: bonus }
              )}
            </span>
          </div>
        ) : (
          <div>
            {translate('epay.gold.members.get')}{' '}
            <span
              className={styles.bonusTokensBasic}
              id={this.makeId(`${pack.quantity}`)}
            >
              {translate(
                'payment.payment.amount.bonus.tokens',
                { 0: goldMemberBonus }
              )}
            </span>
          </div>
        )}
      </Fragment>
    );
  };

  combinedBonusesContent = (addedPercentageBonuses, bonusTokenAmount) => {
    const { pack } = this.props;

    if (!addedPercentageBonuses && !bonusTokenAmount) {
      return undefined;
    }

    const showBoth = Boolean(addedPercentageBonuses) && Boolean(bonusTokenAmount);
    const showPercentageBonus = Boolean(addedPercentageBonuses) && !bonusTokenAmount;
    let bonus = undefined;
    if (showBoth) {
      bonus = translate(
        'payment.payment.amount.bonus.tokens',
        { 0: `${addedPercentageBonuses} + ${bonusTokenAmount} ` }
      );
    } else if (showPercentageBonus) {
      bonus = translate(
        'payment.payment.amount.bonus.tokens',
        { 0: `${addedPercentageBonuses}` }
      );
    } else {
      bonus = translate(
        'payment.payment.amount.bonus.tokens',
        { 0: `${bonusTokenAmount}` });
    }

    return (
      <span
        className={styles.bonusTokensCombined}
        id={this.makeId(`${pack.quantity}`)}
      >
        {bonus}
      </span>
    );
  };

  combinedBonusesForGold = (bonusTokenAmount, goldMemberBonus) => {
    const { pack } = this.props;

    if (!goldMemberBonus) {
      return undefined;
    }

    const showBoth = Boolean(goldMemberBonus) && Boolean(bonusTokenAmount);
    const showGoldBonus = bonusTokenAmount + goldMemberBonus > 0;
    const showPercentageBonus = Boolean(goldMemberBonus) && !bonusTokenAmount;

    let goldBonus = undefined;
    if (showBoth) {
      goldBonus = translate(
        'epay.combined.gold.bonus',
        { 0: `${goldMemberBonus} + ${bonusTokenAmount} ` }
      );
    } else if (showPercentageBonus) {
      goldBonus = translate(
        'epay.combined.gold.bonus',
        { 0: `${goldMemberBonus}` }
      );
    } else {
      goldBonus = translate(
        'epay.combined.gold.bonus',
        { 0: `${bonusTokenAmount}` }
      );
    }

    return (
      showGoldBonus && (
        <span
          className={styles.bonusTokensCombined}
          id={this.makeId(`${pack.quantity}`)}
        >
          {goldBonus}
        </span>
      )
    );
  };

  cryptoExchangeRate = () => {
    const { pack, selectedCryptoCurrency } = this.props;
    const { cryptoData } = selectedCryptoCurrency || {};

    const amountInCrypto = (pack.amount / cryptoData.rate).toFixed(8);

    return (
      <div className={styles.cryptoConversionRate}>
        <span>{amountInCrypto}</span>
        <SubdirectoryArrowLeftIcon className={styles.arrowIcon} />
      </div>
    );
  };

  onClickPack = () => {
    const { pack, setActive } = this.props;

    setActive(pack);
  };

  render() {
    const {
      isActive,
      pack,
      selectedCryptoCurrency,
      selectedMethod,
    } = this.props;

    const cryptoCurrencyActive = selectedMethod.value === 'CR' && selectedCryptoCurrency;
    const processorIdCC = this.getProcessorIdCC();
    const activeCampaign = this.getPrimaryCampaign();
    const bonusTokensCampaign = this.getBonusTokensCampaign();
    const allCampaignsWithPercentageBonus = this.getCampaignsWithPercentageBonus();
    const isGiftCard = this.checkGiftCard();
    // extra bonus e.g. for first purchase
    const bonusTokenAmount = this.getBonusTokensAmount(bonusTokensCampaign, activeCampaign);

    const addedPercentageBonuses = this.calculateTotalPercentageBonus(allCampaignsWithPercentageBonus);

    // total tokens amount user gets (package baseline + bonuses)
    const campaignAmount = this.calculateTotalCampaignAmount(bonusTokenAmount, addedPercentageBonuses);

    return (
      <div
        className={isActive ? `${styles.paymentRow} ${styles.selectedRow}` : styles.paymentRow}
        data-processor-id={processorIdCC}
        id={`${this.makeId(this.IDs.paymentRow)}_${pack.quantity}`}
        onClick={this.onClickPack}
      >
        <div className={styles.checked}>
          {isActive && <Brightness1Icon />}
        </div>
        {this.amountRow(campaignAmount)}
        {!isGiftCard && this.bonusesRow()}
        {cryptoCurrencyActive && this.cryptoExchangeRate()}
      </div>
    );
  }
}
